import { addDays, monthOf, weekday, weekStart, WEEKDAY_SHORT, type Shift } from '@kontor/shared';
import { initials } from '../calendar/PersonDot';

export const dateDe = (d: string) => `${d.slice(8, 10)}.${d.slice(5, 7)}.${d.slice(0, 4)}`;
export const dayLabel = (d: string) => `${WEEKDAY_SHORT[weekday(d)]}, ${dateDe(d)}`;
export const hoursDe = (h: number) => `${h.toLocaleString('de-DE', { maximumFractionDigits: 2 })} Std.`;
/** Uhrzeit ohne führende Null: 08:00 → 8:00 */
export const clock = (t: string) => t.replace(/^0/, '');
/** Schichtzeit wie „8:00 - 16:00“ */
export const shiftTimes = (s: { startTime: string; endTime: string }) => `${clock(s.startTime)} - ${clock(s.endTime)}`;

const MONTHS = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];

export type ShiftView = 'day' | 'week' | 'month';
export const SHIFT_VIEW_LABELS: Record<ShiftView, string> = { day: 'Tag', week: 'Woche', month: 'Monat' };

export interface ShiftPeriod {
  from: string;
  to: string;
  label: string;
  /** Alle Tage von `from` bis `to`: einer, eine Woche (7) oder ein Monat (28 bis 31) */
  days: string[];
}

/** Zeitraum der Ansicht um ein Datum: der Tag, die Woche (Montag bis Sonntag) oder der Kalendermonat */
export function periodOf(view: ShiftView, anchor: string): ShiftPeriod {
  let from: string;
  let to: string;
  let label: string;
  if (view === 'day') {
    from = anchor;
    to = anchor;
    label = dayLabel(anchor);
  } else if (view === 'week') {
    from = weekStart(anchor);
    to = addDays(from, 6);
    label = `${dateDe(from)} bis ${dateDe(to)}`;
  } else {
    const month = monthOf(anchor);
    from = `${month}-01`;
    to = addDays(`${addDays(from, 32).slice(0, 8)}01`, -1);
    label = `${MONTHS[Number(month.slice(5, 7)) - 1]} ${month.slice(0, 4)}`;
  }
  const days: string[] = [];
  for (let d = from; d <= to; d = addDays(d, 1)) days.push(d);
  return { from, to, label, days };
}

/** Einen Tag, eine Woche oder einen Monat vor (n = 1) oder zurück (n = -1) */
export function movePeriod(view: ShiftView, anchor: string, n: 1 | -1): string {
  if (view === 'day') return addDays(anchor, n);
  return view === 'week' ? addDays(anchor, 7 * n) : addDays(`${monthOf(anchor)}-01`, n < 0 ? -1 : 32);
}

/** Feste Farbe je Mitarbeiter aus der ID, gleiche Person gleiche Farbe */
export function employeeHue(id: string): number {
  let hash = 0;
  for (const c of id) hash = (hash * 31 + c.charCodeAt(0)) >>> 0;
  return (hash % 10) * 36;
}

/**
 * Kürzel der Mitarbeiter im Plan: Anfangsbuchstaben von Vor- und Nachname („Anna Schmidt“ → „AS“). Haben zwei Personen dasselbe Kürzel, bekommen sie mehr
 * Buchstaben vom Nachnamen („ASc“, „ASm“), damit man sie im Plan unterscheidet.
 */
export function abbreviations(shifts: Pick<Shift, 'employeeId' | 'employeeName'>[]): Map<string, string> {
  const people = new Map<string, string>();
  for (const s of shifts) people.set(s.employeeId, s.employeeName);
  const base = new Map([...people].map(([id, name]) => [id, initials(name)]));
  const used = new Map<string, number>();
  for (const code of base.values()) used.set(code, (used.get(code) ?? 0) + 1);
  const out = new Map<string, string>();
  const taken = new Set<string>();
  for (const [id, name] of people) {
    let code = base.get(id)!;
    if ((used.get(code) ?? 0) > 1) {
      const parts = name.trim().split(/\s+/).filter(Boolean);
      const last = parts.length > 1 ? parts[parts.length - 1]! : '';
      code = `${(parts[0]?.[0] ?? '?').toUpperCase()}${last.slice(0, 2).toLowerCase().replace(/^./, (c) => c.toUpperCase())}`;
    }
    let unique = code;
    for (let i = 2; taken.has(unique); i += 1) unique = `${code}${i}`;
    taken.add(unique);
    out.set(id, unique);
  }
  return out;
}
